import { getVWOClient, getDecisionData } from './client';
import type { VWOFlagContext, VWOFeatureFlag, VWOFeatureFlags, VWOLogger } from './types';

/**
 * Fetches multiple VWO feature flags in a single batch.
 *
 * Each flag is resolved independently — if one fails, the others
 * still resolve. Failed flags default to disabled with no variables.
 *
 * VWO deterministically buckets the visitor based on the ID,
 * so the same visitor always gets the same variation per flag.
 *
 * Custom variables (e.g., url, brand) are passed to VWO for
 * audience pre-segmentation — allowing non-engineers to target
 * specific URL patterns from the VWO dashboard.
 *
 * Consent gating is opt-in via `context.hasAnalyticsConsent`:
 * - `undefined` (omitted): VWO is called normally. Use when the caller
 *   cannot yet resolve consent (e.g. CDN does not forward the consent
 *   cookie to origin).
 * - `false`: every flag resolves to disabled with no variables and the
 *   VWO client is never contacted. Flag overrides still apply so that
 *   QA/dev workflows are unaffected.
 * - `true`: VWO is called normally.
 *
 * @param flagKeys - Array of feature flag keys to fetch
 * @param context - Visitor ID, VWO credentials, analytics-consent flag, and optional custom variables
 * @param logger - Optional logger for flag resolution events
 *
 * @example
 * const flags = await getFeatureFlags(
 *   ['showScheduler', 'newCheckout'],
 *   {
 *     visitorId: 'D1A2B3C4...',
 *     accountId: '613301',
 *     sdkKey: 'your-sdk-key',
 *     hasAnalyticsConsent: true,
 *     customVariables: { url: '/locations/new-york' },
 *   }
 * );
 *
 * if (flags.showScheduler.isEnabled) {
 *   // render scheduler
 * }
 */
export async function getFeatureFlags<K extends string>(
	flagKeys: K[],
	context: VWOFlagContext,
	logger?: VWOLogger
): Promise<VWOFeatureFlags<K>> {
	const result = {} as VWOFeatureFlags<K>;
	const { visitorId, accountId, sdkKey, customVariables, flagOverrides, hasAnalyticsConsent } = context;

	if (flagKeys.length === 0) return result;

	if (hasAnalyticsConsent === false) {
		logger?.info?.('VWO feature flags skipped — analytics consent not granted', {
			visitorId,
			flagKeys,
		});
		for (const key of flagKeys) {
			if (flagOverrides && key in flagOverrides) {
				result[key] = { isEnabled: true, variables: flagOverrides[key] };
			} else {
				result[key] = { isEnabled: false, variables: {} };
			}
		}
		return result;
	}

	let vwoClient: Awaited<ReturnType<typeof getVWOClient>>;
	try {
		vwoClient = await getVWOClient(accountId, sdkKey, logger);
	} catch (error) {
		logger?.error?.('Failed to initialize VWO client for feature flags', {
			visitorId,
			flagKeys,
			error: error instanceof Error ? error.message : String(error),
		});

		for (const key of flagKeys) {
			result[key] = { isEnabled: false, variables: {} };
		}
		return result;
	}

	const flagPromises = flagKeys.map(async (key) => {
		// Short-circuit with override value
		if (flagOverrides && key in flagOverrides) {
			const variables = flagOverrides[key];
			logger?.info?.(`VWO flag "${key}" overridden`, {
				visitorId,
				flagKey: key,
				variables,
			});
			return { key, flag: { isEnabled: true, variables } as VWOFeatureFlag };
		}

		try {
			const flag = await vwoClient.getFlag(key, {
				id: visitorId,
				customVariables,
			});
			const isEnabled = flag.isEnabled();
			const variables: Record<string, unknown> = {};
			for (const v of flag.getVariables()) {
				const key = v['key'] as string | undefined;
				if (key != null) {
					variables[key] = v['value'];
				}
			}

			const experimentMeta = getDecisionData(visitorId, key);

			logger?.info?.(`VWO flag "${key}" resolved`, {
				visitorId,
				flagKey: key,
				isEnabled,
				variables,
				experimentMeta,
			});

			return { key, flag: { isEnabled, variables, experimentMeta } };
		} catch (error) {
			// Clean up any decision data that the integrations callback may have stored
			getDecisionData(visitorId, key);

			logger?.error?.(`Error fetching VWO flag "${key}"`, {
				visitorId,
				flagKey: key,
				error: error instanceof Error ? error.message : String(error),
			});

			return { key, flag: { isEnabled: false, variables: {} } };
		}
	});

	const resolved = await Promise.all(flagPromises);
	for (const { key, flag } of resolved) {
		result[key] = flag;
	}

	return result;
}
